import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api, { Client, ClientActivity, Deal, PaginatedResponse, formatMMK, ADMIN_ROLES, CRM_WRITE_ROLES, UserRole } from '@/lib/api'
import { useMyPermissions } from '@/hooks/useRolePermissions'
import DataTable from '@/components/DataTable'
import SlideOver from '@/components/SlideOver'
import { Plus, Search, ArrowRightLeft, AlertCircle, Phone, Calendar, CheckSquare, ExternalLink, Clock, UserCheck, Edit3, FolderOpen, FileDown, Star } from 'lucide-react'
import CommentsSection from '@/components/CommentsSection'
import UserSearchSelect from '@/components/UserSearchSelect'
import AuditInfo from '@/components/AuditInfo'

const SOURCES = ['Manual', 'Website', 'Referral', 'Social Media', 'Email Campaign', 'Cold Call', 'Exhibition', 'Other']
const PRIORITIES = ['High', 'Medium', 'Low']
const STAGE_COLORS: Record<string, string> = {
  'Qualification': '#3b82f6', 'Need Analysis': '#8b5cf6', 'Proposal': '#f59e0b',
  'Negotiation': '#ef4444', 'Won': '#10b981', 'Lost': '#6b7280',
}

export default function Contacts() {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<'Lead' | 'Contact'>('Lead')
  const [search, setSearch] = useState('')
  const [source, setSource] = useState('')
  const [priority, setPriority] = useState('')
  const [ownerFilter, setOwnerFilter] = useState('')
  const [vipStatus, setVipStatus] = useState('')
  const [page, setPage] = useState(1)
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [form, setForm] = useState<Partial<Client>>({})
  const [error, setError] = useState('')
  const [contactTab, setContactTab] = useState<'info' | 'activities' | 'deals' | 'projects'>('info')
  const [activitySubTab, setActivitySubTab] = useState<'schedule' | 'log' | 'timeline'>('schedule')
  const [activityForm, setActivityForm] = useState<any>({})
  const [showOwnerChange, setShowOwnerChange] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
  const userRole = currentUser.role as UserRole
  const isAdmin = (ADMIN_ROLES as readonly string[]).includes(userRole) || userRole === 'director'
  const isFinance = userRole === 'finance'
  const { canDo } = useMyPermissions()
  const canWrite = canDo('crm_write')

  useEffect(() => {
    const clientIdParam = searchParams.get('client_id')
    if (!clientIdParam) return
    api.get(`/clients/${clientIdParam}`)
      .then(res => {
        const client = res.data as Client
        setSelectedClient(client)
        setContactTab(client.contact_type === 'Contact' ? 'info' : 'info')
        setTab(client.contact_type === 'Lead' ? 'Lead' : 'Contact')
        setEditMode(false)
        setShowOwnerChange(false)
        setError('')
        setSearchParams({}, { replace: true })
      })
      .catch(() => {})
  }, [])

  const { data: crmOwners } = useQuery<{ id: number; full_name: string; role: string }[]>({
    queryKey: ['crm-owners'],
    queryFn: () => api.get('/users/crm-owners').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })

  const { data, isLoading } = useQuery<PaginatedResponse<Client>>({
    queryKey: ['clients', tab, search, source, priority, ownerFilter, vipStatus, page],
    queryFn: () => api.get('/clients', { params: { contact_type: tab, search, source, priority, owner: ownerFilter, vip_status: vipStatus, page, per_page: 25 } }).then(r => r.data),
  })

  const { data: clientActivities } = useQuery<ClientActivity[]>({
    queryKey: ['client-activities', selectedClient?.id],
    queryFn: () => api.get(`/clients/${selectedClient!.id}/activities`).then(r => r.data),
    enabled: !!selectedClient,
  })

  const { data: clientDeals } = useQuery<PaginatedResponse<Deal>>({
    queryKey: ['client-deals', selectedClient?.id],
    queryFn: () => api.get('/deals', { params: { client_id: selectedClient!.id, per_page: 100 } }).then(r => r.data),
    enabled: !!selectedClient && selectedClient.contact_type === 'Contact',
  })

  const { data: clientProjects } = useQuery<any[]>({
    queryKey: ['client-projects', selectedClient?.id],
    queryFn: () => api.get(`/clients/${selectedClient!.id}/projects`).then(r => r.data),
    enabled: !!selectedClient && selectedClient.contact_type === 'Contact',
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => api.post('/clients', data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['clients'] }); setShowCreate(false); setForm({}); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to create record') },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: any) => api.put(`/clients/${id}`, data),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: ['clients'] }); setSelectedClient(res.data); setEditMode(false); setShowOwnerChange(false); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to update record') },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/clients/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['clients'] }); setSelectedClient(null); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to delete record') },
  })

  const convertMutation = useMutation({
    mutationFn: (id: number) => api.post(`/clients/${id}/convert`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['clients'] }); setSelectedClient(null); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to convert record') },
  })

  const createActivity = useMutation({
    mutationFn: (data: any) => api.post(`/clients/${selectedClient!.id}/activities`, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['client-activities'] }); setActivityForm({}); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to create activity') },
  })

  const canEdit = selectedClient && !isFinance && (isAdmin || currentUser.full_name === selectedClient.owner)

  const columns = [
    { key: 'client_name', label: 'Name', render: (c: Client) => <span className="font-medium text-slate-900 inline-flex items-center gap-1.5">{c.client_name}{c.vip_customer && <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold"><Star size={10} fill="currentColor" /> VIP</span>}</span> },
    { key: 'company', label: 'Company' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'source', label: 'Source', render: (c: Client) => <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{c.source}</span> },
    { key: 'priority', label: 'Priority', render: (c: Client) => (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.priority === 'High' ? 'bg-red-100 text-red-700' : c.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>{c.priority}</span>
    )},
    { key: 'status', label: 'Status', render: (c: Client) => <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-900">{c.status}</span> },
  ]

  const renderLeadDetail = () => (
    <div className="space-y-5">
      {error && <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200"><AlertCircle size={16} className="shrink-0" /><span>{error}</span></div>}
      <div className="space-y-3">
        {[
          ['Company', selectedClient!.company],
          ['Email', selectedClient!.email],
          ['Phone', selectedClient!.phone],
          ['Source', selectedClient!.source],
          ['Priority', selectedClient!.priority],
          ['Status', selectedClient!.status],
          ['Type', selectedClient!.contact_type],
          ['VIP Customer', selectedClient!.vip_customer ? 'Yes' : 'No'],
          ['Owner', selectedClient!.owner || '—'],
        ].map(([label, value]) => (
          <div key={label as string} className="flex justify-between text-sm">
            <span className="text-slate-500">{label}</span>
            <span className="text-slate-900 font-medium">{value || '—'}</span>
          </div>
        ))}
        {canEdit && (
          <div className="bg-slate-50 rounded-lg p-3">
            <UserSearchSelect
              label="Change Owner"
              value=""
              onChange={(name) => {
                if (name) {
                  updateMutation.mutate({ id: selectedClient!.id, owner: name })
                }
              }}
              placeholder="Search for new owner..."
            />
          </div>
        )}
        {selectedClient!.notes && (
          <div className="text-sm">
            <span className="text-slate-500">Notes</span>
            <p className="text-slate-700 mt-1">{selectedClient!.notes}</p>
          </div>
        )}
        <AuditInfo
          createdByName={selectedClient!.created_by_name}
          createdAt={selectedClient!.created_at}
          updatedAt={selectedClient!.updated_at}
        />
      </div>
      <CommentsSection entityType="client" entityId={selectedClient!.id} />
      {clientActivities && clientActivities.filter(a => a.activity_type.startsWith('System:')).length > 0 && (
        <div className="border-t border-slate-200 pt-3">
          <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Audit Trail</div>
          <div className="space-y-2">
            {clientActivities.filter(a => a.activity_type.startsWith('System:')).map(a => (
              <div key={a.id} className="flex gap-2 p-2 bg-slate-50 rounded-lg text-xs">
                <div className={`mt-0.5 p-1 rounded ${a.activity_type.includes('Created') ? 'bg-green-100 text-green-600' : a.activity_type.includes('Owner') ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'}`}>
                  {a.activity_type.includes('Created') ? <Plus size={12} /> : a.activity_type.includes('Owner') ? <UserCheck size={12} /> : <Edit3 size={12} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-slate-900 font-medium">{a.subject}</div>
                  <div className="text-slate-400">{a.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {canWrite && (
        <div className="flex gap-2 pt-3 border-t border-slate-200">
          {canEdit && (
            <button onClick={() => { setEditMode(true); setForm(selectedClient!) }}
              className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900">Edit</button>
          )}
          {canEdit && (
            <button onClick={() => convertMutation.mutate(selectedClient!.id)}
              className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 flex items-center justify-center gap-1">
              <ArrowRightLeft size={14} /> Convert
            </button>
          )}
          {canEdit && (
            <button onClick={() => { if (confirm('Delete this record?')) deleteMutation.mutate(selectedClient!.id) }}
              className="py-2 px-4 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100">Delete</button>
          )}
        </div>
      )}
    </div>
  )

  const renderContactDetail = () => (
    <div className="space-y-4">
      {error && <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200"><AlertCircle size={16} className="shrink-0" /><span>{error}</span></div>}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {(['info', 'activities', 'deals', 'projects'] as const).map(t => (
          <button key={t} onClick={() => setContactTab(t)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${contactTab === t ? 'border-red-800 text-red-800' : 'border-transparent text-slate-500'}`}>
            {t === 'info' ? 'Info' : t === 'activities' ? 'Activities' : t === 'deals' ? 'Deals' : 'Projects'}
          </button>
        ))}
      </div>

      {contactTab === 'info' && !editMode && (
        <div className="space-y-4">
          <div className="space-y-3">
            {[
              ['Company', selectedClient!.company],
              ['Email', selectedClient!.email],
              ['Phone', selectedClient!.phone],
              ['Source', selectedClient!.source],
              ['Priority', selectedClient!.priority],
              ['Status', selectedClient!.status],
              ['Type', selectedClient!.contact_type],
              ['VIP Customer', selectedClient!.vip_customer ? 'Yes' : 'No'],
            ].map(([label, value]) => (
              <div key={label as string} className="flex justify-between text-sm">
                <span className="text-slate-500">{label}</span>
                <span className="text-slate-900 font-medium">{value || '—'}</span>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center text-sm border-t border-slate-100 pt-3">
            <span className="text-slate-500">Owner</span>
            <div className="flex items-center gap-2">
              <span className="text-slate-900 font-medium">{selectedClient!.owner || '—'}</span>
              {canEdit && !showOwnerChange && (
                <button onClick={() => setShowOwnerChange(true)}
                  className="text-xs text-red-800 hover:text-red-900 font-medium">Change</button>
              )}
            </div>
          </div>

          {showOwnerChange && (
            <div className="bg-slate-50 rounded-lg p-3 space-y-2">
              <UserSearchSelect
                label="New Owner"
                value=""
                onChange={(name) => {
                  if (name) {
                    updateMutation.mutate({ id: selectedClient!.id, owner: name })
                  }
                }}
                placeholder="Search for new owner..."
              />
              <button type="button" onClick={() => setShowOwnerChange(false)}
                className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
            </div>
          )}

          {selectedClient!.notes && (
            <div className="text-sm">
              <span className="text-slate-500">Notes</span>
              <p className="text-slate-700 mt-1">{selectedClient!.notes}</p>
            </div>
          )}

          <AuditInfo
            createdByName={selectedClient!.created_by_name}
            createdAt={selectedClient!.created_at}
            updatedAt={selectedClient!.updated_at}
          />

          <CommentsSection entityType="client" entityId={selectedClient!.id} />

          <div className="flex gap-2 pt-3 border-t border-slate-200">
            {canEdit && (
              <button onClick={() => { setEditMode(true); setForm(selectedClient!) }}
                className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900">Edit</button>
            )}
            {canWrite && (
              <button onClick={() => { window.location.href = '/deals' }}
                className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 flex items-center justify-center gap-1">
                <Plus size={14} /> Create Deal
              </button>
            )}
            {canEdit && (
              <button onClick={() => { if (confirm('Delete this record?')) deleteMutation.mutate(selectedClient!.id) }}
                className="py-2 px-4 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100">Delete</button>
            )}
          </div>
        </div>
      )}

      {contactTab === 'info' && editMode && (
        <form onSubmit={e => { e.preventDefault(); updateMutation.mutate({ id: selectedClient!.id, ...form }) }} className="space-y-4">
          {[
            { key: 'client_name', label: 'Name', type: 'text' },
            { key: 'company', label: 'Company', type: 'text' },
            { key: 'email', label: 'Email', type: 'email' },
            { key: 'phone', label: 'Phone', type: 'text' },
          ].map(f => (
            <div key={f.key}>
              <label className="block text-sm font-medium text-slate-700 mb-1">{f.label}</label>
              <input type={f.type} value={(form as any)[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
            </div>
          ))}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
            <select value={form.source || ''} onChange={e => setForm({ ...form, source: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
              {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
            <select value={form.priority || ''} onChange={e => setForm({ ...form, priority: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!form.vip_customer} onChange={e => setForm({ ...form, vip_customer: e.target.checked })}
              className="rounded border-slate-300 text-amber-600 focus:ring-amber-500" />
            <Star size={14} className="text-amber-500" fill="currentColor" /> Mark as VIP customer
          </label>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900">Save</button>
            <button type="button" onClick={() => setEditMode(false)} className="py-2 px-4 text-sm bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200">Cancel</button>
          </div>
        </form>
      )}

      {contactTab === 'activities' && (
        <div className="space-y-4">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
            {(['schedule', 'log', 'timeline'] as const).map(t => (
              <button key={t} onClick={() => setActivitySubTab(t)}
                className={`flex-1 px-3 py-1.5 text-sm rounded-md transition-colors ${activitySubTab === t ? 'bg-white shadow-sm font-medium text-slate-900' : 'text-slate-500'}`}>
                {t === 'schedule' ? 'Schedule' : t === 'log' ? 'Log Activity' : 'Timeline'}
              </button>
            ))}
          </div>

          {activitySubTab === 'schedule' && (
            <form onSubmit={e => { e.preventDefault(); createActivity.mutate(activityForm); setActivityForm({}) }} className="space-y-3">
              <div><label className="text-sm font-medium text-slate-700 block mb-1">Type</label>
                <select value={activityForm.activity_type || ''} onChange={e => setActivityForm({ ...activityForm, activity_type: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" required>
                  <option value="">Select...</option>
                  <option>Scheduled Call</option><option>Scheduled Meeting</option><option>Task</option>
                </select></div>
              <div><label className="text-sm font-medium text-slate-700 block mb-1">Subject</label>
                <input value={activityForm.subject || ''} onChange={e => setActivityForm({ ...activityForm, subject: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" required /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700 block mb-1">Date</label>
                  <input type="date" value={activityForm.activity_date || ''} onChange={e => setActivityForm({ ...activityForm, activity_date: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
                <div><label className="text-sm font-medium text-slate-700 block mb-1">Time</label>
                  <input type="time" value={activityForm.activity_time || ''} onChange={e => setActivityForm({ ...activityForm, activity_time: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
              </div>
              <div><label className="text-sm font-medium text-slate-700 block mb-1">Description</label>
                <textarea value={activityForm.description || ''} onChange={e => setActivityForm({ ...activityForm, description: e.target.value })} rows={2}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
              <button type="submit" disabled={createActivity.isPending}
                className="w-full py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">Schedule Activity</button>
            </form>
          )}

          {activitySubTab === 'log' && (
            <form onSubmit={e => { e.preventDefault(); createActivity.mutate({ ...activityForm, status: 'Completed' }); setActivityForm({}) }} className="space-y-3">
              <div><label className="text-sm font-medium text-slate-700 block mb-1">Type</label>
                <select value={activityForm.activity_type || ''} onChange={e => setActivityForm({ ...activityForm, activity_type: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" required>
                  <option value="">Select...</option>
                  <option>Completed Call</option><option>Completed Meeting</option>
                </select></div>
              <div><label className="text-sm font-medium text-slate-700 block mb-1">Subject</label>
                <input value={activityForm.subject || ''} onChange={e => setActivityForm({ ...activityForm, subject: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" required /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700 block mb-1">Date</label>
                  <input type="date" value={activityForm.activity_date || ''} onChange={e => setActivityForm({ ...activityForm, activity_date: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
                <div><label className="text-sm font-medium text-slate-700 block mb-1">Time</label>
                  <input type="time" value={activityForm.activity_time || ''} onChange={e => setActivityForm({ ...activityForm, activity_time: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
              </div>
              <div><label className="text-sm font-medium text-slate-700 block mb-1">Result/Outcome</label>
                <textarea value={activityForm.result || ''} onChange={e => setActivityForm({ ...activityForm, result: e.target.value })} rows={3}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" /></div>
              <button type="submit" disabled={createActivity.isPending}
                className="w-full py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">Log Activity</button>
            </form>
          )}

          {activitySubTab === 'timeline' && (
            <div className="space-y-3">
              {(!clientActivities || clientActivities.length === 0) ? (
                <p className="text-sm text-slate-500 text-center py-4">No activities yet</p>
              ) : clientActivities.map(a => (
                a.activity_type.startsWith('System:') ? (
                  <div key={a.id} className="flex gap-2 p-2 bg-slate-50 rounded-lg text-xs">
                    <div className={`mt-0.5 p-1 rounded ${a.activity_type.includes('Created') ? 'bg-green-100 text-green-600' : a.activity_type.includes('Owner') ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'}`}>
                      {a.activity_type.includes('Created') ? <Plus size={12} /> : a.activity_type.includes('Owner') ? <UserCheck size={12} /> : <Edit3 size={12} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-900 font-medium">{a.subject}</div>
                      <div className="text-slate-400">{a.description}</div>
                    </div>
                  </div>
                ) : (
                <div key={a.id} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className={`mt-0.5 p-1.5 rounded-lg ${a.activity_type.includes('Call') ? 'bg-red-100 text-red-800' : a.activity_type.includes('Meeting') ? 'bg-purple-100 text-purple-600' : 'bg-amber-100 text-amber-600'}`}>
                    {a.activity_type.includes('Call') ? <Phone size={14} /> : a.activity_type.includes('Meeting') ? <Calendar size={14} /> : <CheckSquare size={14} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-medium text-slate-900">{a.subject}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${a.status === 'Completed' ? 'bg-green-100 text-green-700' : a.status === 'Cancelled' ? 'bg-red-100 text-red-700' : 'bg-red-100 text-red-900'}`}>{a.status}</span>
                    </div>
                    <div className="text-xs text-slate-500">{a.activity_type} {a.activity_date ? `on ${a.activity_date}` : ''} {a.activity_time || ''}</div>
                    {a.description && <div className="text-sm text-slate-600 mt-1">{a.description}</div>}
                    {a.result && <div className="text-sm text-slate-700 mt-1 bg-white p-2 rounded border border-slate-200">{a.result}</div>}
                  </div>
                </div>
                )
              ))}
            </div>
          )}
        </div>
      )}

      {contactTab === 'deals' && (
        <div className="space-y-3">
          {(!clientDeals?.data || clientDeals.data.length === 0) ? (
            <p className="text-sm text-slate-500 text-center py-4">No deals associated with this contact</p>
          ) : clientDeals.data.map(deal => (
            <div key={deal.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:shadow-sm transition-shadow">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-slate-900">{deal.deal_name}</span>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium text-white" style={{ backgroundColor: STAGE_COLORS[deal.stage] || '#6b7280' }}>{deal.stage}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span className="font-semibold text-slate-700">{formatMMK(Number(deal.value))}</span>
                <span>{deal.owner || '—'}</span>
              </div>
              {deal.expected_close && (
                <div className="text-xs text-slate-400 mt-1">Close: {deal.expected_close}</div>
              )}
            </div>
          ))}
          <button onClick={() => { window.location.href = '/deals' }}
            className="w-full py-2 text-sm bg-red-50 text-red-800 rounded-lg hover:bg-red-100 flex items-center justify-center gap-1 font-medium">
            <ExternalLink size={14} /> Go to Deals
          </button>
        </div>
      )}

      {contactTab === 'projects' && (
        <div className="space-y-3">
          {(!clientProjects || clientProjects.length === 0) ? (
            <div className="text-center py-8 text-sm text-slate-500">
              <FolderOpen size={32} className="mx-auto mb-2 text-slate-300" />
              No projects linked to this contact
            </div>
          ) : clientProjects.map((proj: any) => (
            <div key={proj.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono px-1.5 py-0.5 bg-slate-200 text-slate-700 rounded">{proj.project_type}</span>
                  <span className="text-sm font-medium text-slate-900">{proj.project_code}</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${
                  proj.status === 'Complete' ? 'bg-green-100 text-green-700' :
                  proj.status === 'In-Progress' ? 'bg-blue-100 text-blue-700' :
                  proj.status === 'Procurement' ? 'bg-amber-100 text-amber-700' :
                  'bg-slate-100 text-slate-600'
                }`}>{proj.status}</span>
              </div>
              {proj.description && (
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{proj.description}</p>
              )}
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-slate-400">{proj.date_completed ? `Completed: ${proj.date_completed}` : proj.installation_start_date ? `Started: ${proj.installation_start_date}` : '—'}</span>
                <a href={`/projects?open=${proj.id}`}
                  className="text-xs text-red-700 hover:text-red-900 font-medium flex items-center gap-1">
                  View <ExternalLink size={11} />
                </a>
              </div>
            </div>
          ))}
          <button onClick={() => { window.location.href = '/projects' }}
            className="w-full py-2 text-sm bg-red-50 text-red-800 rounded-lg hover:bg-red-100 flex items-center justify-center gap-1 font-medium">
            <ExternalLink size={14} /> Go to Project Bank
          </button>
        </div>
      )}
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Leads & Contacts</h1>
        <div className="flex items-center gap-2">
          <button onClick={async () => {
            try {
              const res = await api.get('/export/clients', { params: { contact_type: tab, search, vip_status: vipStatus }, responseType: 'blob' })
              const url = URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = 'clients.xlsx'; a.click(); URL.revokeObjectURL(url)
            } catch {}
          }} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600" title="Export to Excel">
            <FileDown size={15} /> <span className="hidden sm:inline">Export</span>
          </button>
          {canWrite && (
            <button onClick={() => { setShowCreate(true); setForm({ contact_type: tab, status: 'Active', source: 'Manual', priority: 'Medium' }); setError('') }}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-red-800 text-white rounded-lg text-sm font-medium hover:bg-red-900 transition-colors min-h-[44px]">
              <Plus size={16} /> New {tab}
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-200 overflow-x-auto">
        {(['Lead', 'Contact'] as const).map(t => (
           <button key={t} onClick={() => { setTab(t); setPage(1); setSearch(''); setSource(''); setPriority(''); setOwnerFilter(''); setVipStatus('') }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap min-h-[44px] ${tab === t ? 'border-red-800 text-red-800' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {t}s
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-3 border-b border-slate-200 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <input type="text" placeholder={`Search ${tab.toLowerCase()}s...`} value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
          </div>
          <select value={source} onChange={e => { setSource(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
            <option value="">All Sources</option>
            {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={priority} onChange={e => { setPriority(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
            <option value="">All Priorities</option>
            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {crmOwners && crmOwners.length > 1 && (
            <select value={ownerFilter} onChange={e => { setOwnerFilter(e.target.value); setPage(1) }}
              className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
              <option value="">All Owners</option>
              {crmOwners.map(o => <option key={o.id} value={o.full_name}>{o.full_name}</option>)}
            </select>
          )}
          <select value={vipStatus} onChange={e => { setVipStatus(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
            <option value="">All Customers</option>
            <option value="VIP">VIP Customers</option>
            <option value="Non-VIP">Non-VIP Customers</option>
          </select>
        </div>
        <DataTable columns={columns} data={data?.data || []} total={data?.total || 0}
          page={page} totalPages={data?.total_pages || 1} onPageChange={setPage}
          onRowClick={c => { setSelectedClient(c); setContactTab('info'); setActivitySubTab('schedule'); setEditMode(false); setShowOwnerChange(false); setError('') }} selectedId={selectedClient?.id}
          getRowId={c => c.id} loading={isLoading} emptyMessage={`No ${tab.toLowerCase()}s found`} />
      </div>

      <SlideOver open={!!selectedClient} onClose={() => { setSelectedClient(null); setEditMode(false); setShowOwnerChange(false); setError('') }}
        title={selectedClient?.client_name || ''} wide={selectedClient?.contact_type === 'Contact'}>
        {selectedClient && selectedClient.contact_type === 'Lead' && !editMode && renderLeadDetail()}
        {selectedClient && selectedClient.contact_type === 'Contact' && renderContactDetail()}
        {selectedClient && selectedClient.contact_type === 'Lead' && editMode && (
          <form onSubmit={e => { e.preventDefault(); updateMutation.mutate({ id: selectedClient.id, ...form }) }} className="space-y-4">
            {error && <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200"><AlertCircle size={16} className="shrink-0" /><span>{error}</span></div>}
            {[
              { key: 'client_name', label: 'Name', type: 'text' },
              { key: 'company', label: 'Company', type: 'text' },
              { key: 'email', label: 'Email', type: 'email' },
              { key: 'phone', label: 'Phone', type: 'text' },
            ].map(f => (
              <div key={f.key}>
                <label className="block text-sm font-medium text-slate-700 mb-1">{f.label}</label>
                <input type={f.type} value={(form as any)[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
              </div>
            ))}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
              <select value={form.source || ''} onChange={e => setForm({ ...form, source: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
                {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
              <select value={form.priority || ''} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!form.vip_customer} onChange={e => setForm({ ...form, vip_customer: e.target.checked })}
                className="rounded border-slate-300 text-amber-600 focus:ring-amber-500" />
              <Star size={14} className="text-amber-500" fill="currentColor" /> Mark as VIP customer
            </label>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
              <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="flex-1 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900">Save</button>
              <button type="button" onClick={() => setEditMode(false)} className="py-2 px-4 text-sm bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200">Cancel</button>
            </div>
          </form>
        )}
      </SlideOver>

      <SlideOver open={showCreate} onClose={() => { setShowCreate(false); setError('') }} title={`New ${tab}`}>
        <form onSubmit={e => { e.preventDefault(); createMutation.mutate(form) }} className="space-y-4">
          {error && <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200"><AlertCircle size={16} className="shrink-0" /><span>{error}</span></div>}
          {[
            { key: 'client_name', label: 'Name *', type: 'text', required: true },
            { key: 'company', label: 'Company', type: 'text' },
            { key: 'email', label: 'Email', type: 'email' },
            { key: 'phone', label: 'Phone', type: 'text' },
          ].map(f => (
            <div key={f.key}>
              <label className="block text-sm font-medium text-slate-700 mb-1">{f.label}</label>
              <input type={f.type} value={(form as any)[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" required={f.required} />
            </div>
          ))}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
            <select value={form.source || 'Manual'} onChange={e => setForm({ ...form, source: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
              {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
            <select value={form.priority || 'Medium'} onChange={e => setForm({ ...form, priority: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!form.vip_customer} onChange={e => setForm({ ...form, vip_customer: e.target.checked })}
              className="rounded border-slate-300 text-amber-600 focus:ring-amber-500" />
            <Star size={14} className="text-amber-500" fill="currentColor" /> Mark as VIP customer
          </label>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
          </div>
          <button type="submit" disabled={createMutation.isPending}
            className="w-full py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">
            {createMutation.isPending ? 'Creating...' : `Create ${tab}`}
          </button>
        </form>
      </SlideOver>
    </div>
  )
}
